import React from 'react';
import { CheckoutConfig, CheckoutTemplateId } from '../../../types';

export const TEMPLATES: { id: CheckoutTemplateId; label: string; desc: string; category: string }[] = [
  { id: 'saas', label: 'SaaS Vibe', desc: 'Modern split-view gradient look favored by high-growth startups.', category: 'SaaS & Modern' },
  { id: 'classic', label: 'Classic Pro', desc: 'Corporate enterprise split-view with dark navy header.', category: 'Enterprise' },
  { id: 'minimal', label: 'Minimalist Centered', desc: 'Pure focus single-column card with clean typography.', category: 'Minimalist' },
  { id: 'glass', label: 'Glassmorphism', desc: 'Frosted glass container with ambient mesh glows.', category: 'SaaS & Modern' },
  { id: 'cyberpunk', label: 'Cyberpunk Dark', desc: 'Ultra-dark aesthetic with neon accents and monospace text.', category: 'High-Tech' },
  { id: 'bento', label: 'Bento Grid', desc: 'Modern mosaic grid with distinct modular content zones.', category: 'SaaS & Modern' },
  { id: 'split_dark', label: 'Inverse Split', desc: 'Dark order summary paired with high-contrast form.', category: 'High-Tech' },
  { id: 'floating', label: 'Floating Pop', desc: 'Elevated floating modal over warm vibrant gradient.', category: 'SaaS & Modern' },
  { id: 'vertical', label: 'Vertical Stack', desc: 'Linear top-to-bottom step flow optimized for fast mobile checkout.', category: 'Minimalist' },
  { id: 'neumorphic', label: 'Neumorphic Soft', desc: 'Soft embossed tactile UI with dual inset shadows.', category: 'Specialty' },
  { id: 'feature_rich', label: 'Feature Matrix', desc: 'Comprehensive plan value showcase with side-by-side details.', category: 'Enterprise' },
  { id: 'mono', label: 'Industrial Mono', desc: 'Brutalist high-contrast monochrome design with hard borders.', category: 'High-Tech' },
  { id: 'playful', label: 'Playful Brand', desc: 'Vibrant bouncy aesthetic with rounded shapes and sticker badges.', category: 'Specialty' },
  { id: 'clean_stripe', label: 'Stripe Checkout', desc: 'Streamlined minimal sidebar with hyper-clean inputs.', category: 'Minimalist' },
  { id: 'social_heavy', label: 'Social Proof Plus', desc: 'High-converting layout prioritizing reviews and trust seals.', category: 'Enterprise' },
];

interface TemplatesTabProps {
  config: CheckoutConfig;
  onUpdate: (partial: Partial<CheckoutConfig>) => void;
}

export const CheckoutStudioTemplatesTab: React.FC<TemplatesTabProps> = ({ config, onUpdate }) => {
  const [filter, setFilter] = React.useState<string>('All');
  const categories = ['All', 'SaaS & Modern', 'Enterprise', 'Minimalist', 'High-Tech', 'Specialty'];

  const filteredTemplates = filter === 'All' ? TEMPLATES : TEMPLATES.filter((t) => t.category === filter);

  return (
    <div className="space-y-4">
      {/* Category Pills */}
      <div className="flex flex-wrap gap-1.5 pb-1 border-b border-slate-200 dark:border-slate-800">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setFilter(cat)}
            className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all ${
              filter === cat
                ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-sm'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Grid of Templates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {filteredTemplates.map((template) => {
          const isSelected = config.template === template.id;
          return (
            <div
              key={template.id}
              onClick={() => onUpdate({ template: template.id })}
              className={`p-3.5 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between text-left ${
                isSelected
                  ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/30 shadow-md ring-2 ring-indigo-500/20'
                  : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-900'
              }`}
            >
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-900 dark:text-white">{template.label}</h4>
                  {isSelected && (
                    <span className="w-2 h-2 rounded-full bg-indigo-600 ring-4 ring-indigo-100 dark:ring-indigo-900"></span>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight">{template.desc}</p>
              </div>

              <div className="mt-3 flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                <span className="text-[9px] font-bold uppercase text-slate-400">{template.category}</span>
                <span className={`text-[10px] font-extrabold ${isSelected ? 'text-indigo-600' : 'text-slate-400'}`}>
                  {isSelected ? 'Active Theme' : 'Apply'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
